import type { FC, PropsWithChildren } from 'react'

import styled from '@emotion/styled'

import ImageComponent from '@segment/matcha/components/Image'
import ColumnTrack from '@segment/matcha/components/ColumnTrack'

import SectionBase, * as Section from '@segment/matcha/recipes/Section'
import CopyTextBase, * as CopyText from '@segment/matcha/recipes/CopyText'

import iso27001Png2x from './assets/Iso27001@2x.png'
import iso27001Webp from './assets/Iso27001.webp'
import iso27001Webp2x from './assets/Iso27001@2x.webp'

import iso27017Png2x from './assets/Iso27017@2x.png'
import iso27017Webp from './assets/Iso27017.webp'
import iso27017Webp2x from './assets/Iso27017@2x.webp'

import iso27018Png2x from './assets/Iso27018@2x.png'
import iso27018Webp from './assets/Iso27018.webp'
import iso27018Webp2x from './assets/Iso27018@2x.webp'

import aicpaSocBadgePng2x from './assets/AicpaSocBadge@2x.png'
import aicpaSocBadgeWebp from './assets/AicpaSocBadge.webp'
import aicpaSocBadgeWebp2x from './assets/AicpaSocBadge@2x.webp'

const copy = {
  heading: `Certifications and attestations`,
  description: `Segment’s security and privacy program is based on and aligned with industry-standard frameworks, and we maintain a comprehensive suite of certifications and attestations to further demonstrate our commitment to security and privacy.`,
}
const link = {
  text: `View documentation`,
  url: `https://security.segment.com 
  `,
}

const Certifications: FC<PropsWithChildren<unknown>> = () => {
  return (
    <Wrapper>
      <FirstSectionBase variant="centered">
        <Section.Copy>
          <CopyTextBase centered={true}>
            <CopyText.Heading>{copy.heading}</CopyText.Heading>
            <CopyText.Description>{copy.description}</CopyText.Description>
            <CopyText.Link href={link.url} target="_blank">
              {link.text}
            </CopyText.Link>
          </CopyTextBase>
        </Section.Copy>
      </FirstSectionBase>
      <SecondSectionBase variant="noCopy">
        <Section.Contents>
          <StyledColumnTrack columns={1} overrides={{ lg: 4 }}>
            <Image
              alt="ISO 27001"
              images={{ webp: iso27001Webp, webp2x: iso27001Webp2x, png2x: iso27001Png2x }}
              size={[120, 140]}
            />
            <Image
              alt="ISO 27017"
              images={{ webp: iso27017Webp, webp2x: iso27017Webp2x, png2x: iso27017Png2x }}
              size={[116, 140]}
            />
            <Image
              alt="ISO 27018"
              images={{ webp: iso27018Webp, webp2x: iso27018Webp2x, png2x: iso27018Png2x }}
              size={[119, 140]}
            />
            <Image
              alt="SOC 2 Type 2"
              images={{
                webp: aicpaSocBadgeWebp,
                webp2x: aicpaSocBadgeWebp2x,
                png2x: aicpaSocBadgePng2x,
              }}
              size={[96, 96]}
            />
          </StyledColumnTrack>
        </Section.Contents>
      </SecondSectionBase>
    </Wrapper>
  )
}

interface ImageProps {
  images: {
    png2x: string
    webp: string
    webp2x: string
  }
  alt: string
  size?: readonly [number, number]
}

const Image: FC<PropsWithChildren<ImageProps>> = ({ images, alt, size = [179, 120] }) => {
  const [w, h] = size
  return (
    <ImageWrapper>
      <picture>
        <source type="image/webp" srcSet={`${images.webp}, ${images.webp2x} 2x`} />
        <ImageComponent width={w} height={h} src={images.png2x} alt={alt} loading="lazy" />
      </picture>
    </ImageWrapper>
  )
}

const StyledColumnTrack = styled(ColumnTrack)`
  max-width: 690px;
  margin: 0 auto;
`

const ImageWrapper = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;
`

const Wrapper = styled.div`
  ${({ theme }) => theme.breakpoints.down(`md`)} {
    max-width: 720px;
    margin: auto;
  }
`

const FirstSectionBase = styled(SectionBase)`
  ${Section.Wrapper} {
    row-gap: 0;
    ${({ theme }) => theme.breakpoints.up(`lg`)} {
      padding-bottom: 60px;
    }
  }
`

const SecondSectionBase = styled(SectionBase)`
  ${Section.Wrapper} {
    ${({ theme }) => theme.breakpoints.up(`lg`)} {
      padding-top: 0;
    }
  }
`

export default Certifications
